/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * Our Story content page.
 *
 * Served at `/our-story.html` via the dedicated route `src/routes/_app.our-story[.html].tsx`,
 * which carries the `@PageType`/`@RegionDefinition` authoring metadata so `generate-cartridge`
 * emits `route: "/our-story.html"` (see W-23754187). React Router ranks that STATIC route above
 * the dynamic `/:contentId.html` content route — proven by `content-route-precedence.test.ts` —
 * so requests resolve there. `_app.$contentId[.html].tsx` retains an `our-story` branch in its
 * `CUSTOM_CONTENT_PAGES` set as a ranking-independent safety net that renders this same component.
 *
 * The server data-load (`loadOurStoryData`) lives in the sibling `./our-story-page.data.ts`,
 * which keeps this file exporting only a component (fast refresh).
 */
import type { ReactElement } from 'react';
import heroImage from '/images/our-story/hero.jpg';
import manhattanImage from '/images/our-story/manhattan.jpg';
import securtexImage from '/images/our-story/securtex.jpg';
import modernEverywhereImage from '/images/our-story/modern-everywhere.jpg';
import travelImage from '/images/our-story/travel.jpg';
import mediaRecognitionImage from '/images/our-story/media-recognition.jpg';
import { Link } from '@/components/link';
import { DynamicImage } from '@/components/dynamic-image';
import {
    Breadcrumb,
    BreadcrumbList,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbPage,
    BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { SeoMeta } from '@/components/seo-meta';
import { Region } from '@/components/region';
import { CategoryHrefProvider } from '@/components/navigation-menu/category-href';
import type { OurStoryPageData } from './our-story-page.data';

const COLLECTIONS = [
    {
        title: 'The Manhattan Collection',
        tagline: 'The one that makes everything feel a little more put together — instantly.',
        body: 'Our premium collection crafted in luxe twill and finished with elevated hardware. Made to be styled from day to night and complete with our signature organization — as always.',
        img: manhattanImage,
    },
    {
        title: 'The Securtex® Anti-Theft Collection',
        tagline: 'Peace of mind, built right in. Move confidently through your day.',
        body: 'Our award-winning, proprietary anti-theft technology is seamlessly built into every bag for peace of mind. Designed to keep your essentials secure and your mind at ease.',
        img: securtexImage,
    },
    {
        title: 'The Modern Everywhere Collection',
        tagline: 'The one you grab. Every single time. Lightweight, washable, endlessly versatile.',
        body: 'Our core, best-selling collection of do-it-all everyday bags, made from water-repellent, machine-washable materials — designed to make everyday feel easier.',
        img: modernEverywhereImage,
    },
    {
        title: 'The Travel Collection',
        tagline: 'Founded by two flight attendants, Baggallini was built on real travel experience.',
        body: 'Every style is designed with our signature organization, lightweight materials, and thoughtful functionality — so you can move confidently through every journey.',
        img: travelImage,
    },
];

function OurStoryContent() {
    return (
        <>
            {/* Hero */}
            {/* isolate: without it, the -z-10 image/overlay below have no local stacking context to
                anchor to, so they escape to whichever distant ancestor does establish one and can end
                up painted behind unrelated page content instead of just behind the h1/p here.
                objectFit="contain" (not "cover"): the campaign photo's subjects don't survive a hard
                crop at this section's wide aspect ratio — cover was cutting heads off. Letting the
                whole photo show, letterboxed on a solid brand-dark fill, is preferred over a full-bleed
                crop that clips the subjects. */}
            <div className="relative isolate flex flex-col items-center justify-center py-24 sm:py-32 text-center px-6 aspect-[4/3] sm:aspect-[16/9] overflow-hidden bg-[#1A1A1A]">
                <DynamicImage
                    src={heroImage}
                    alt=""
                    widths={{ base: '100vw' }}
                    priority="high"
                    objectFit="contain"
                    className="absolute inset-0 w-full h-full -z-10"
                    imageProps={{ className: 'w-full h-full' }}
                />
                <div className="absolute inset-0 bg-black/35 -z-10" />
                <h1
                    style={{ fontFamily: 'var(--font-display)' }}
                    className="text-5xl sm:text-7xl font-light tracking-wide text-primary-foreground mb-4">
                    Stay Organized. Stay Unstoppable.
                </h1>
                <p className="text-lg text-primary-foreground/90 max-w-2xl leading-relaxed uppercase tracking-[0.08em]">
                    Our bags are built around one core idea: organization as empowerment.
                </p>
            </div>

            {/* Mission statement */}
            <div className="max-w-3xl mx-auto px-6 py-16 text-center">
                <p
                    style={{ fontFamily: 'var(--font-display)' }}
                    className="text-3xl lg:text-4xl font-light text-[#1A1A1A] leading-relaxed mb-6">
                    &quot;We design organized bags to simplify life.&quot;
                </p>
                <p className="text-base text-muted-foreground leading-relaxed">
                    So you can move through the world feeling prepared and confident — because when everything has a
                    place, you can spend less time searching and more time living.
                </p>
            </div>

            {/* Collections */}
            <div className="max-w-screen-xl mx-auto px-6 pb-24 space-y-0">
                {COLLECTIONS.map((item, i) => {
                    const isEven = i % 2 === 0;
                    return (
                        <div
                            key={item.title}
                            className={`flex flex-col ${isEven ? 'md:flex-row' : 'md:flex-row-reverse'} items-center`}>
                            <div className="w-full md:w-1/2 aspect-square overflow-hidden">
                                <DynamicImage
                                    src={item.img}
                                    alt={item.title}
                                    widths={{ base: '100vw', md: '50vw' }}
                                    className="w-full h-full"
                                    imageProps={{ className: 'w-full h-full' }}
                                />
                            </div>
                            <div
                                className={`w-full md:w-1/2 px-8 py-12 md:py-16 ${isEven ? 'bg-[#F5F3F0]' : 'bg-background'}`}>
                                <h2
                                    style={{ fontFamily: 'var(--font-display)' }}
                                    className="text-3xl font-light mb-2 text-[#1A1A1A]">
                                    {item.title}
                                </h2>
                                <p className="text-base font-medium text-[#1A1A1A] leading-relaxed mb-4">
                                    {item.tagline}
                                </p>
                                <p className="text-base text-muted-foreground leading-relaxed mb-6">{item.body}</p>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Media recognition */}
            <div className="max-w-screen-xl mx-auto px-6 pb-24 text-center">
                <h2 style={{ fontFamily: 'var(--font-display)' }} className="text-3xl font-light mb-8 text-[#1A1A1A]">
                    Recognized by Leading Global Media
                </h2>
                <div className="max-w-sm mx-auto">
                    <DynamicImage
                        src={mediaRecognitionImage}
                        alt="Baggallini media recognition logos"
                        widths={{ base: '80vw', sm: '384px' }}
                        objectFit="contain"
                        className="w-full h-auto"
                        imageProps={{ className: 'w-full h-auto' }}
                    />
                </div>
            </div>

            {/* Collections CTA */}
            <div className="bg-[#1A1A1A] py-16 text-center text-primary-foreground px-6">
                <h2 style={{ fontFamily: 'var(--font-display)' }} className="text-5xl font-light mb-4">
                    Find Your Perfect Bag
                </h2>
                <p className="text-primary-foreground/70 mb-8 max-w-xl mx-auto">
                    From everyday crossbodies to travel-ready totes, there&apos;s a Baggallini built for the way you
                    live.
                </p>
                {/* RGBARRY-CUSTOMIZATION: hardcoded slug — no category id is available for this leaf
                    category (Shop by Style > Shop All), so it can't go through useCategoryHref's
                    id-keyed nav map like the root CTA this replaced. Link still gets the site/locale
                    prefix applied automatically. */}
                <Link
                    to="/shop-bags/shop-by-style/shop-all/"
                    className="inline-flex items-center gap-3 bg-background text-[#1A1A1A] text-sm tracking-[0.12em] uppercase font-medium px-9 py-4 hover:bg-background/90 transition-colors rounded-none">
                    Shop All Bags
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                </Link>
            </div>
        </>
    );
}

export function OurStoryPage({ loaderData }: { loaderData: OurStoryPageData }): ReactElement {
    return (
        <CategoryHrefProvider value={loaderData.categoryUrls}>
            <div className="pb-8">
                <SeoMeta
                    title="About Baggallini — Our Story"
                    description="Learn about Baggallini — organized bags designed to simplify life so women can move through the world feeling confident and in control."
                    openGraph={{ type: 'article', url: loaderData.pageUrl, image: loaderData.ogImageUrl }}
                />

                <div className="max-w-screen-2xl mx-auto px-4 pt-4 pb-2">
                    <Breadcrumb>
                        <BreadcrumbList>
                            <BreadcrumbItem>
                                <BreadcrumbLink asChild>
                                    <Link to="/">Home</Link>
                                </BreadcrumbLink>
                            </BreadcrumbItem>
                            <BreadcrumbSeparator />
                            <BreadcrumbItem>
                                <BreadcrumbPage>Our Story</BreadcrumbPage>
                            </BreadcrumbItem>
                        </BreadcrumbList>
                    </Breadcrumb>
                </div>

                {loaderData.page ? (
                    <Region page={loaderData.page} regionId="headline" errorElement={<OurStoryContent />} />
                ) : (
                    <OurStoryContent />
                )}
            </div>
        </CategoryHrefProvider>
    );
}
